import React from 'react';
import { cn } from '../lib/utils';
import Prism3D from './Prism3D';
import AnimaTicketIcon from './AnimaTicketIcon';

type CurrencyType = 'coin' | 'gem' | 'prism' | 'heart' | 'ticket_star' | 'ticket_heart' | 'exp';

interface CurrencyIconProps {
  type: CurrencyType;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  count?: number;
  showCount?: boolean;
  stars?: number;
  color?: string;
}

export default function CurrencyIcon({ 
  type, 
  size = 'md', 
  className, 
  count, 
  showCount = false,
  stars,
  color
}: CurrencyIconProps) {
  const sizeClasses = {
    sm: 'w-4 h-4 text-[10px]',
    md: 'w-6 h-6 text-xs',
    lg: 'w-10 h-10 text-xl',
    xl: 'w-16 h-16 text-3xl'
  };

  return (
    <div className={cn("inline-flex items-center gap-1.5", className)}>
      <div className={cn("relative shrink-0", sizeClasses[size])}>
        {type === 'coin' ? (
          /* Premium 3D Claymorphism Gold Coin */
          <div className="w-full h-full rounded-full bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-500 shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),inset_0_-4px_6px_rgba(0,0,0,0.2),0_4px_10px_rgba(0,0,0,0.2)] relative overflow-hidden clay-icon">
            {/* The Glint */}
            <div className="absolute top-1 left-1.5 w-1/3 h-1/4 bg-white/40 rounded-full blur-[1px] rotate-[-20deg]" />
            {/* Inner Ring with depth */}
            <div className="absolute inset-1.5 rounded-full border border-white/20 shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)]" />
          </div>
        ) : type === 'gem' ? (
          /* 3D Hoshi Star Gem (CC) */
          <div className="w-full h-full relative clay-icon">
            <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_4px_6px_rgba(0,0,0,0.3)]">
              <defs>
                <linearGradient id="ccStarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#22d3ee" />
                  <stop offset="100%" stopColor="#0891b2" />
                </linearGradient>
              </defs>
              {/* Star Shadow Rim */}
              <path 
                d="M 50 10 L 70 32 L 95 42 L 80 65 L 75 92 L 50 80 L 25 92 L 20 65 L 5 42 L 30 32 Z" 
                fill="#0e7490" 
                transform="translate(0, 3)" 
              />
              {/* Main Star Body */}
              <path 
                d="M 50 10 L 70 32 L 95 42 L 80 65 L 75 92 L 50 80 L 25 92 L 20 65 L 5 42 L 30 32 Z" 
                fill="url(#ccStarGrad)" 
                stroke="#06b6d4" 
                strokeWidth="2" 
              />
              {/* 3D Folds */}
              <path d="M 50 52 L 30 32 L 50 10 Z" fill="white" fillOpacity="0.3" />
              <path d="M 50 52 L 5 42 L 30 32 Z" fill="white" fillOpacity="0.15" />
              <path d="M 50 52 L 50 10 L 70 32 Z" fill="white" fillOpacity="0.1" />
              <path d="M 50 52 L 70 32 L 95 42 Z" fill="white" fillOpacity="0.15" />
            </svg>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-cyan-400/20 blur-xl rounded-full -z-10 animate-pulse" />
          </div>
        ) : type === 'prism' ? (
          <AnimaTicketIcon 
            stars={stars || 1} 
            size={size === 'sm' ? 'xs' : size === 'xl' ? 'xl' : size === 'lg' ? 'lg' : 'md'}
            className="w-full h-full"
          />
        ) : type === 'heart' ? (
          /* 3D Glossy Heart */
          <div className="w-full h-full relative clay-icon">
            <svg viewBox="0 0 24 24" className="w-full h-full drop-shadow-[0_4px_4px_rgba(0,0,0,0.3)]">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#f43f5e" stroke="#be123c" strokeWidth="0.5" />
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="white" fillOpacity="0.2" transform="scale(0.8) translate(3, 3)" />
            </svg>
          </div>
        ) : type === 'ticket_star' || type === 'ticket_heart' ? (
          /* 3D Claymorphism Ticket */
          <div className="w-full h-full relative clay-icon">
            <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_4px_6px_rgba(0,0,0,0.3)]">
              <defs>
                <linearGradient id="starTicketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" />
                  <stop offset="100%" stopColor="#d97706" />
                </linearGradient>
                <linearGradient id="heartTicketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#fb7185" />
                  <stop offset="100%" stopColor="#e11d48" />
                </linearGradient>
              </defs>
              {/* Ticket Body with 'Punched' Sides */}
              <path 
                d="M 10 20 Q 10 10 20 10 L 80 10 Q 90 10 90 20 L 90 40 Q 80 50 90 60 L 90 80 Q 90 90 80 90 L 20 90 Q 10 90 10 80 L 10 60 Q 20 50 10 40 Z" 
                fill={type === 'ticket_star' ? "url(#starTicketGrad)" : "url(#heartTicketGrad)"}
                stroke={type === 'ticket_star' ? "#92400e" : "#9f1239"}
                strokeWidth="2"
              />
              {/* Highlight Line */}
              <path d="M 20 18 L 80 18" stroke="white" strokeOpacity="0.3" strokeWidth="3" strokeLinecap="round" />
              {/* Center Symbol */}
              {type === 'ticket_star' ? (
                <path d="M 50 35 L 55 45 L 65 45 L 57 53 L 60 63 L 50 57 L 40 63 L 43 53 L 35 45 L 45 45 Z" fill="white" fillOpacity="0.8" />
              ) : (
                <path d="M 50 65 L 47 62 C 38 53 32 48 32 41 C 32 35 36 31 42 31 C 45 31 48 33 50 35 C 52 33 55 31 58 31 C 64 31 68 35 68 41 C 68 48 62 53 53 62 Z" fill="white" fillOpacity="0.8" />
              )}
              {/* Perforation Line */}
              <line x1="25" y1="20" x2="25" y2="80" stroke="white" strokeOpacity="0.2" strokeWidth="2" strokeDasharray="4 4" />
            </svg>
          </div>
        ) : type === 'exp' ? (
          /* Premium 3D Claymorphism EXP Shield Badge */
          <div className="w-full h-full relative clay-icon flex items-center justify-center">
            <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_4px_6px_rgba(0,0,0,0.3)]">
              <defs>
                <linearGradient id="expGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="100%" stopColor="#4f46e5" />
                </linearGradient>
                <filter id="glowExp" x="-25%" y="-25%" width="150%" height="150%">
                  <feGaussianBlur stdDeviation="3.5" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
              {/* Outer hexagonal shield shadow */}
              <polygon 
                points="50,5 92,25 92,75 50,95 8,75 8,25" 
                fill="#312e81" 
                transform="translate(0, 3)" 
              />
              {/* Main Shield Body */}
              <polygon 
                points="50,5 92,25 92,75 50,95 8,75 8,25" 
                fill="url(#expGrad)" 
                stroke="#6366f1" 
                strokeWidth="3.5" 
              />
              {/* Inner Shield border */}
              <polygon 
                points="50,12 85,29 85,71 50,88 15,71 15,29" 
                fill="none" 
                stroke="white" 
                strokeOpacity="0.25" 
                strokeWidth="2" 
              />
              {/* EXP Text */}
              <text 
                x="50" 
                y="60" 
                fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" 
                fontWeight="900" 
                fontSize="27" 
                fontStyle="italic" 
                fill="white" 
                textAnchor="middle" 
                filter="url(#glowExp)"
                letterSpacing="-1.5"
                style={{ textShadow: '0 2.5px 5px rgba(0,0,0,0.45)' }}
              >
                EXP
              </text>
              {/* Glint Sparkle */}
              <path d="M 50 15 L 52 22 L 59 24 L 52 26 L 50 33 L 48 26 L 41 24 L 48 22 Z" fill="white" fillOpacity="0.85" />
            </svg>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-indigo-500/20 blur-xl rounded-full -z-10 animate-pulse" />
          </div>
        ) : null}
      </div>
      
      {showCount && count !== undefined && (
        <span className={cn(
          "font-num font-black tabular-nums",
          type === 'coin' ? "text-yellow-600" : type === 'gem' ? "text-cyan-600" : "text-cyan-600"
        )}>
          {count.toLocaleString()}
        </span>
      )}
    </div>
  );
}
